import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="privacy-page">
      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="privacy-hero-content"
          >
            <div className="privacy-hero-badge">
              <Shield className="privacy-hero-badge-icon" />
              <span>Data Protection</span>
            </div>
            <h1 className="privacy-hero-title">
              Privacy
              <span className="privacy-hero-title-gradient"> Policy</span>
            </h1>
            <p className="privacy-hero-subtitle">
              We are committed to protecting your privacy and personal information. Learn how we collect, use, and safeguard your data.
            </p>
            <div className="privacy-hero-date">
              Last updated: January 15, 2024
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="privacy-content-section">
        <div className="privacy-content-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="privacy-content"
          >
            
            <div className="privacy-section">
              <h2 className="privacy-section-title">1. Information We Collect</h2>
              <p className="privacy-section-text">
                We collect information you provide directly to us, information we obtain automatically when you use our services, and information from third parties.
              </p>
              <ul className="privacy-list">
                <li><strong>Personal Information:</strong> Name, email address, phone number, address</li>
                <li><strong>Financial Information:</strong> Bank account details, trading history, transaction data</li>
                <li><strong>Technical Information:</strong> IP address, browser type, device information</li>
                <li><strong>Usage Information:</strong> How you interact with our platform and services</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">2. How We Use Your Information</h2>
              <p className="privacy-section-text">
                We use your information to provide, maintain, and improve our services, comply with legal obligations, and communicate with you.
              </p>
              <ul className="privacy-list">
                <li>Process transactions and manage your trading account</li>
                <li>Verify your identity and prevent fraud</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Send important updates about our services</li>
                <li>Comply with regulatory requirements</li>
                <li>Improve our platform and develop new features</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">3. Information Sharing</h2>
              <p className="privacy-section-text">
                We do not sell or rent your personal information to third parties. We may share your information in limited circumstances:
              </p>
              <ul className="privacy-list">
                <li>With service providers who help us operate our business</li>
                <li>To comply with legal obligations or regulatory requirements</li>
                <li>To protect our rights, privacy, safety, or property</li>
                <li>In connection with business transfers or mergers</li>
                <li>With your explicit consent</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">4. Data Security</h2>
              <p className="privacy-section-text">
                We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction.
              </p>
              <div className="privacy-security-features">
                <div className="privacy-security-item">
                  <Lock className="privacy-security-icon" />
                  <div>
                    <strong>Encryption:</strong> All data is encrypted in transit and at rest using industry-standard protocols.
                  </div>
                </div>
                <div className="privacy-security-item">
                  <Shield className="privacy-security-icon" />
                  <div>
                    <strong>Access Controls:</strong> Strict access controls limit who can view your personal information.
                  </div>
                </div>
                <div className="privacy-security-item">
                  <Eye className="privacy-security-icon" />
                  <div>
                    <strong>Monitoring:</strong> We continuously monitor our systems for security threats and vulnerabilities.
                  </div>
                </div>
              </div>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">5. Your Rights and Choices</h2>
              <p className="privacy-section-text">
                You have certain rights regarding your personal information, including:
              </p>
              <ul className="privacy-list">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">6. Cookies and Tracking</h2>
              <p className="privacy-section-text">
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content.
              </p>
              <ul className="privacy-list">
                <li><strong>Essential Cookies:</strong> Required for basic platform functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use our platform</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">7. International Transfers</h2>
              <p className="privacy-section-text">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">8. Data Retention</h2>
              <p className="privacy-section-text">
                We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">9. Children's Privacy</h2>
              <p className="privacy-section-text">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">10. Changes to This Policy</h2>
              <p className="privacy-section-text">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.
              </p>
            </div>

            <div className="privacy-section">
              <h2 className="privacy-section-title">11. Contact Us</h2>
              <p className="privacy-section-text">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="privacy-contact">
                <p><strong>Email:</strong> privacy@chartlinebrokerage.com</p>
                <p><strong>Phone:</strong> +1 239 791 0799</p>
                <p><strong>Address:</strong> 2268 S Tongass Hwy, Ketchikan, Alaska(AK), 99901</p>
                <p><strong>Data Protection Officer:</strong> dpo@chartlinebrokerage.com</p>
              </div>
            </div>

          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="privacy-cta-section">
        <div className="privacy-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="privacy-cta-content"
          >
            <Shield className="privacy-cta-icon" />
            <h2 className="privacy-cta-title">
              Questions About Your Privacy?
            </h2>
            <p className="privacy-cta-subtitle">
              Our privacy team is here to help you understand how we protect your data.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="privacy-cta-button"
            >
              Contact Privacy Team
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy; 